/**
 * The other names a class row answers to.
 *
 * A generic row is named for the class it stands in for — `2-Row Pale Malt`,
 * `Carapils/Dextrin Malt`, `Pale Ale Malt (Maris Otter)` — and a brewer rarely
 * writes the whole of that. They write `2-Row`, or `Carapils`, or `Maris
 * Otter`, and every one of those is also the exact product name of somebody's
 * branded row. Scored on the words alone the brand wins at 1.00 and the class
 * row trails on the words it carries and the brewer did not type, so a recipe
 * that named nobody's malt in particular lands on Malteurop's, or Briess's, or
 * Warminster's.
 *
 * This table says what the trade also calls each class row, so that the
 * search and the importer can score those spellings as the row's own name.
 * An alias is a *name* — what somebody writes on a recipe meaning this class
 * and nothing narrower — not a search tag: `base` and `high-enzyme` describe
 * the row, they do not name it. A spelling that is merely a different way to
 * write the same word (`Acid` for `Acidulated`, `Two` for `2`) belongs in the
 * importer's synonym fold, where it applies to every maltster's row alike; an
 * alias belongs here only when it names the class and not the word.
 *
 * Keyed by id, like {@link MALTSTER_REACH} is keyed by maker, and checked by
 * `class-names.test.ts`: every key is a `Generic` row, no alias repeats the
 * row's own name, and no spelling is claimed by two rows.
 */
export const CLASS_ALIASES: Readonly<Record<string, readonly string[]>> = {
  // Three maltsters sell a product called exactly `2-Row`; the class is what
  // a file means when it says no more than that. `Two-Row` is Prairie's
  // spelling, and the type-ahead has no synonym fold to reach `2` from it.
  'generic-2-row-pale': ['2-Row', 'Two-Row'],
  // The variety is the class: a file that says `Maris Otter` and nothing else
  // is not asking for Warminster's.
  'generic-maris-otter': ['Maris Otter'],
  // Briess's trademark has become the trade's word for the dextrin malt, and
  // BeerSmith's database spells the class `Cara-Pils/Dextrine`.
  'generic-carapils': ['Carapils', 'Cara-Pils/Dextrine', 'Dextrin Malt'],
  // The older English name, which BeerSmith writes as `Black (Patent) Malt`.
  'generic-black': ['Black Patent Malt'],
  // The German name, which is how Weyermann's and Sugar Creek's are sold.
  'generic-smoked': ['Rauchmalz'],
  // `Acid Malt` is the everyday name and `Sauermalz` the German one.
  'generic-acidulated': ['Acid Malt', 'Sauermalz'],
};

/** The other names the trade writes for a class row; empty for any other row. */
export function classAliases(id: string): readonly string[] {
  return CLASS_ALIASES[id] ?? [];
}
